import { useEffect } from 'react';
import type { AuctionEngine } from '../game/engine';
import { MIN_PRICE } from '../game/engine';
import type { Offset } from '../game/geom';
import type { RoundSetup } from '../game/themes';
import { itemImagePath, type ThemeVisual } from '../game/visual';
import type { T } from '../i18n/strings';
import { BuildCanvas } from '../components/BuildCanvas';
import { Button, PLAY_HOST } from '../components/ui';
import { PLAYER_COLORS } from '../theme';

interface Props {
  t: T;
  engine: AuctionEngine;
  round: RoundSetup;
  visual: ThemeVisual | null;
  names: string[];
  moved: Map<number, Offset>[];
  onMove: (player: number, itemIndex: number, centre: Offset) => void;
  popIn: number | null;
  onBuy: (player: number) => void;
  onPrice: (price: number) => void;
  onUndo: () => void;
  canUndo: boolean;
  onMenu: () => void;
  showLink: boolean;
  toast: string | null;
}

/// Landscape auction like the app: the two builds left and right, the item
/// in the middle, buyer buttons and the price slider below.
export function Auction(p: Props) {
  const { t, engine: e, round, visual, names } = p;
  const wonOf = (player: number) => e.builds[player].map((w) => round.sources[w.itemIndex]);
  const namesOf = (player: number) => e.builds[player].map((w) => w.name);

  // Keyboard: ← → price, 1 / 2 sell, Z undo.
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      const el = ev.target as HTMLElement;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      if (ev.metaKey || ev.ctrlKey || ev.altKey) return;
      const k = ev.key.toLowerCase();
      if (k === 'arrowleft' || k === 'arrowdown' || k === '-') p.onPrice(e.price - 1);
      else if (k === 'arrowright' || k === 'arrowup' || k === '+' || k === '=') p.onPrice(e.price + 1);
      else if (k === '1' || k === 'q') p.onBuy(0);
      else if (k === '2' || k === 'p') p.onBuy(1);
      else if (k === 'z' || k === 'backspace') p.canUndo && p.onUndo();
      else if (k === 'escape') p.onMenu();
      else return;
      ev.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const itemIndex = round.sources[e.currentItemIndex];
  const thumb = visual && itemIndex !== undefined ? itemImagePath(visual, itemIndex) : null;
  const max = e.maxPrice;

  const pane = (player: number) => (
    <div className={`pane pane-${player}`}>
      <div className="pane-head" style={{ color: PLAYER_COLORS[player] }}>
        <span className="pane-name">{names[player]}</span>
        <span className="pane-money">${e.wallets[player]}</span>
      </div>
      <BuildCanvas
        visual={visual}
        won={wonOf(player)}
        names={namesOf(player)}
        moved={p.moved[player]}
        onMove={(i, c) => p.onMove(player, i, c)}
        popIn={p.popIn}
        accent={PLAYER_COLORS[player]}
        className="pane-build"
      />
    </div>
  );

  return (
    <div className="auction">
      <header className="stage-top">
        <button className="icon-btn" onClick={p.onMenu} aria-label={t.t('menu')}>
          ☰
        </button>
        <div className="stage-title">{round.theme.theme}</div>
        <Button kind="ghost" onClick={p.onUndo} disabled={!p.canUndo} className="undo">
          ↶ {t.t('undo')}
        </Button>
      </header>

      <div className="auction-row">
        {pane(0)}
        {e.isComplete ? (
          <div className="item-card done" key="done">
            <div className="item-progress">
              {e.itemCount}/{e.itemCount}
            </div>
            <div className="item-done">🏁</div>
            <div className="item-name">{t.a('twentyChallengeWhoWon')}</div>
          </div>
        ) : (
          <div className="item-card" key={e.currentItemIndex}>
            <div className="item-progress">
              {e.currentItemIndex + 1}/{e.itemCount}
            </div>
            {thumb ? (
              <div className="item-img">
                <img src={thumb} alt="" />
              </div>
            ) : (
              <div className="item-img text-only" />
            )}
            <div className={`item-name ${thumb ? '' : 'big'}`}>{e.currentItem}</div>
          </div>
        )}
        {pane(1)}
      </div>

      <div className="auction-controls">
        <BuyerButton t={t} e={e} player={0} name={names[0]} onBuy={p.onBuy} />
        <div className="price">
          <div className="price-value">${e.price}</div>
          <input
            type="range"
            className="price-slider"
            min={MIN_PRICE}
            max={max}
            step={1}
            value={Math.min(e.price, max)}
            disabled={max <= MIN_PRICE}
            onChange={(ev) => p.onPrice(Number(ev.target.value))}
            style={{ ['--fill' as string]: `${max > MIN_PRICE ? ((e.price - MIN_PRICE) / (max - MIN_PRICE)) * 100 : 0}%` }}
            aria-label="Price"
          />
          <div className="price-ticks" aria-hidden="true">
            {Array.from({ length: max - MIN_PRICE + 1 }, (_, i) => (
              <span key={i} className={i + MIN_PRICE <= e.price ? 'on' : ''} />
            ))}
          </div>
          <div className="keys-hint">{t.t('keys')}</div>
        </div>
        <BuyerButton t={t} e={e} player={1} name={names[1]} onBuy={p.onBuy} />
      </div>

      {p.toast && <div className="toast">{p.toast}</div>}
      {p.showLink && <div className="watermark">{PLAY_HOST}</div>}
    </div>
  );
}

function BuyerButton({
  t,
  e,
  player,
  name,
  onBuy,
}: {
  t: T;
  e: AuctionEngine;
  player: number;
  name: string;
  onBuy: (p: number) => void;
}) {
  const can = e.canBuy(player);
  const opener = player === e.opener;
  return (
    <div className="buyer">
      <div className="buyer-hint">{opener ? `▶ ${t.a('twentyChallengeFirstBid')}` : ''}</div>
      <button
        className={`btn buyer-btn p${player + 1}`}
        disabled={!can}
        onClick={() => onBuy(player)}
        style={{ ['--pc' as string]: PLAYER_COLORS[player] }}
      >
        <span className="buyer-key">{player + 1}</span>
        <span className="buyer-name">{name}</span>
        <span className="buyer-money">${e.wallets[player]}</span>
      </button>
    </div>
  );
}
